// Configuration service for managing environment variables
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

export interface AppConfig {
  firebase: FirebaseConfig;
  geminiApiKey: string;
  environment: 'development' | 'staging' | 'production';
  isDevelopment: boolean;
  isStaging: boolean;
  isProduction: boolean;
}

const getFirebaseConfig = (): FirebaseConfig => ({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
});

const getEnvironment = (): 'development' | 'staging' | 'production' => {
  const env = import.meta.env.VITE_APP_ENV || 'development';
  return ['development', 'staging', 'production'].includes(env) 
    ? env as 'development' | 'staging' | 'production'
    : 'development';
};

export const config: AppConfig = {
  firebase: getFirebaseConfig(),
  geminiApiKey: import.meta.env.VITE_GEMINI_API_KEY || '',
  environment: getEnvironment(),
  get isDevelopment() { return this.environment === 'development'; },
  get isStaging() { return this.environment === 'staging'; },
  get isProduction() { return this.environment === 'production'; }
};

// Validation helper
export const validateConfig = (): boolean => {
  console.log('Firebase config validation:', {
    apiKey: config.firebase.apiKey?.substring(0, 10) + '...',
    projectId: config.firebase.projectId,
    appId: config.firebase.appId?.substring(0, 10) + '...',
    environment: config.environment
  });
  
  const required = [
    config.firebase.apiKey,
    config.firebase.projectId,
    config.firebase.appId
  ];
  
  const missing = required.filter(field => !field || field === '');
  
  if (missing.length > 0) {
    console.error('Missing required configuration:', missing);
    return false;
  }
  
  console.log('Firebase configuration is valid');
  return true;
};

// Environment-specific logging
export const log = (message: string, data?: any) => {
  if (config.isDevelopment) {
    console.log(`[DEV] ${message}`, data);
  } else if (config.isStaging) {
    console.log(`[STAGING] ${message}`, data);
  } else {
    console.log(`[PROD] ${message}`);
  }
};
